import { __ } from '@wordpress/i18n';
import { MediaPlaceholder } from '@wordpress/block-editor';
import { useDispatch, useSelect } from '@wordpress/data';
import { createBlock } from '@wordpress/blocks';

/**
 * Re-inserts a core/image inner block when the author has deleted it.
 * Preserves any existing heading/paragraph siblings via prepend.
 *
 * Accepts both a media-library pick and a plain image URL ("Insert from URL").
 * URL-only images carry no attachment id, so the server emits the raw src with
 * zero dimensions and view.js measures the real size when the lightbox loads it.
 */
export default function EmptyImagePlaceholder({ clientId }) {
  const { replaceInnerBlocks } = useDispatch('core/block-editor');
  const existingInner = useSelect(
    (select) =>
      select('core/block-editor').getBlock(clientId)?.innerBlocks ?? [],
    [clientId]
  );

  const prependImage = (attributes) => {
    replaceInnerBlocks(
      clientId,
      [createBlock('core/image', attributes), ...existingInner],
      false
    );
  };

  const onSelect = (media) => {
    if (!media?.id) {
      return;
    }
    prependImage({
      id: media.id,
      url: media.url,
      alt: media.alt || '',
      sizeSlug: 'large',
    });
  };

  // External images have no attachment, so no `id` and no `sizeSlug` — core
  // renders the URL verbatim.
  const onSelectURL = (url) => {
    if (!url) {
      return;
    }
    prependImage({ url });
  };

  return (
    <MediaPlaceholder
      onSelect={onSelect}
      onSelectURL={onSelectURL}
      accept="image/*"
      allowedTypes={['image']}
      labels={{
        title: __('Image', 'brandy-blocks'),
        instructions: __(
          'Pick an image from your library, upload one, or insert one from a URL.',
          'brandy-blocks'
        ),
      }}
    />
  );
}
